import { useState, useEffect, useCallback } from 'react'
import { useFormState } from 'hooks/useFormState'
import { useVestigioService } from 'hooks/useVestigioService'
import { useDescricaoTecnicaVestigioService } from 'hooks/useDescricaoTecnicaVestigioService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'

const schema = {
  tipoVestigioGenerico: {
    presence: { allowEmpty: false, message: '^A natureza genérica é obrigatória' },
  },
  tipoVestigioEspecifico: {
    presence: { allowEmpty: false, message: '^A natureza específica é obrigatória' },
  },
}

const tiposVestigiosSemModelo = ['GERAL', 'BIOLOGICO', 'ELETRONICO', 'PAPILOSCOPICO', 'DOCUMENTAL', 'MERCEOLOGICO']

export const useFormEditarDescricaoTecnica = ({
  idVestigio,
  idExameRealizado,
  setOpenEditarDescricaoTecnica,
  fetchDescricoesTecnicasDeVestigio,
  arquivos,
  setAtualizado,
  descricaoADuplicar,
  setDescricaoADuplicar,
  editarDescricao,
  setEditarDescricao,
}) => {
  const vestigioService = useVestigioService()
  const descricaoTecnicaVestigioService = useDescricaoTecnicaVestigioService()
  const exameRealizadoService = useExameRealizadoService()

  const [tiposVestigios, setTiposVestigios] = useState([])
  const [tiposVestigiosEspecificos, setTiposVestigiosEspecificos] = useState([])
  const [processandoTiposVestigios, setProcessandoTiposVestigios] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [isFormularioInvalido, setIsFormularioInvalido] = useState(false)
  const [exameRealizado, setExameRealizado] = useState({})
  const [processandoExameRealizado, setProcessandoExameRealizado] = useState(false)

  const formDescricaoTecnica = useFormState(schema, {
    tipoVestigioGenerico: null,
    tipoVestigioEspecifico: null,
    unidades: 1,
    unidadesConsumidas: 0,
    descricao: null,
    quantidade: 0,
    unidadeQuantidade: null,
    tipoQuantidade: null,
    quantificacaoContraprova: 'RESTANTE',
    quantidadeContraprova: 0,
    unidadeQuantidadeContraprova: null,
    lacreContraprova: null,
    anexo: 'NAO',
    lacreAnexo: null,
    tipoEmbalagemPrimaria: null,
    outroTipoEmbalagemPrimaria: null,
    corEmbalagemPrimaria: null,
    outraCorEmbalagemPrimaria: null,
    tipoFechoEmbalagemPrimaria: null,
    outroTipoFechoEmbalagemPrimaria: null,
    logomarcaEmbalagemPrimaria: null,
    tipoLogomarcaEmbalagemPrimaria: null,
    corMaterial: null,
    outraCorMaterial: null,
    logomarcaSubstancia: null,
    tipoLogomarcaSubstancia: null,
    outroAspectoFisico: null,
    aspFisics: [],
    arquivos: [],

    //BALÍSTICO
    tipo: null,
    marca: null,
    modelo: null,
    calibreNominal: null,
    calibreVariacao: null,
    calibreCompativel: null,
    medidaMassa: null,
    medidaDiametro: null,
    medidaComprimento: null,
    locFabs: [],
    localFabricacaoInput: null,
    numeracaoDeSerie: null,
    locGravNumSers: [],
    localGravacaoNumeracaoSerieInput: null,
    numeracaoDeMontagem: null,
    locGravNumMons: [],
    localGravacaoNumeracaoMontagemInput: null,
    sisPercs: [],
    sistemaPercussaoInput: null,
    sisCarrs: [],
    sistemaCarregamentoInput: null,
    sistemaFuncionamento: null,
    almaCanos: [],
    almaCanoInput: null,
    comprimentoCano: null,
    acabamento: null,
    tambores: [],
    tamborInput: null,
    carregadores: [],
    carregadorInput: null,
    capacidade: null,
    mecanDisps: [],
    mecanismoDisparoInput: null,
    massaMira: null,
    alcaMira: null,
    empunhaduras: [],
    empunhaduraInput: null,
    coronhas: [],
    coronhaInput: null,
    telhas: [],
    telhaInput: null,
    guardaMao: null,
    gravacoes: [],
    gravacoesInput: null,
    outrasCaracteristicas: null,
    fabricante: null,
    coletaPadrao: null,
    estadoConservacao: null,

    capsulaEsps: [],
    capsulaEspoletamentoInput: null,
    tipoEstojos: [],
    tipoEstojoInput: null,
    tiposProjetil: [],
    tipoProjetilInput: null,
    ressaltosCavados: [],
    ressaltosCavadosInput: null,
    tiposRaiamentos: [],
    tiposRaiamentosInput: null,
    tipoFabricacao: null,
  })

  const fetchTiposVestigios = useCallback(async () => {
    try {
      const response = await vestigioService.listarTiposVestigiosAsync(setProcessandoTiposVestigios)
      setTiposVestigios(response || [])
    } catch (error) {
      console.error('Error fetching tipos vestigios:', error)
    }
  }, []) // Excluded vestigioService to prevent loop

  const fetchExameRealizado = useCallback(async () => {
    try {
      const response = await exameRealizadoService.listarExameRealizadoDetalhadoAsync(
        idExameRealizado,
        setProcessandoExameRealizado
      )
      setExameRealizado(response || {})
    } catch (error) {
      console.error('Error fetching exame realizado:', error)
    }
  }, [idExameRealizado]) // Excluded exameRealizadoService to prevent loop

  const preencherFormularioDuplicacaoVestigio = useCallback(async () => {
    if (descricaoADuplicar?.id) {
      try {
        const descricao = await descricaoTecnicaVestigioService.listarDescricaoTecnicaVestigioDadosEspecificosAsync(
          descricaoADuplicar.id,
          descricaoADuplicar.tipoVestigioGenerico,
          setProcessandoOperacao
        )
        if (!descricao) return

        switch (descricao.tipoVestigioGenerico) {
          case 'BALISTICO':
            formDescricaoTecnica.setFormState((prevState) => ({
              ...prevState,
              values: {
                ...prevState.values,
                tipoVestigioGenerico: descricao.tipoVestigioGenerico,
                tipoVestigioEspecifico: descricao.tipoVestigioEspecifico,
                unidades: descricao.unidades,
                unidadesConsumidas: descricao.unidadesConsumidas,
                descricao: descricao.descricao,
                quantidade: descricao.descVestBal?.quantidade || 0,
                unidadeQuantidade: descricao.descVestBal?.unidadeQuantidade || null,
                embalagemPrimaria: descricao.descVestBal?.embalagemPrimaria || null,
                tipo: descricao.descVestBal?.tipo || null,
                marca: descricao.descVestBal?.marca || null,
                modelo: descricao.descVestBal?.modelo || null,
                calibreNominal: descricao.descVestBal?.calibreNominal || null,
                calibreVariacao: descricao.descVestBal?.calibreVariacao || null,
                calibreCompativel: descricao.descVestBal?.calibreCompativel || null,
                medidaMassa: descricao.descVestBal?.medidaMassa || null,
                medidaDiametro: descricao.descVestBal?.medidaDiametro || null,
                medidaComprimento: descricao.descVestBal?.medidaComprimento || null,
                locFabs: descricao.descVestBal?.locFabs || [],
                localFabricacaoInput: descricao.descVestBal?.localFabricacaoInput || null,
                numeracaoDeSerie: descricao.descVestBal?.numeracaoDeSerie || null,
                locGravNumSers: descricao.descVestBal?.locGravNumSers || [],
                localGravacaoNumeracaoSerieInput: descricao.descVestBal?.localGravacaoNumeracaoSerieInput || null,
                numeracaoDeMontagem: descricao.descVestBal?.numeracaoDeMontagem || null,
                locGravNumMons: descricao.descVestBal?.locGravNumMons || [],
                localGravacaoNumeracaoMontagemInput: descricao.descVestBal?.localGravacaoNumeracaoMontagemInput || null,
                sisPercs: descricao.descVestBal?.sisPercs || [],
                sistemaPercussaoInput: descricao.descVestBal?.sistemaPercussaoInput || null,
                sisCarrs: descricao.descVestBal?.sisCarrs || [],
                sistemaCarregamentoInput: descricao.descVestBal?.sistemaCarregamentoInput || null,
                sistemaFuncionamento: descricao.descVestBal?.sistemaFuncionamento || null,
                almaCanos: descricao.descVestBal?.almaCanos || [],
                almaCanoInput: descricao.descVestBal?.almaCanoInput || null,
                comprimentoCano: descricao.descVestBal?.comprimentoCano || null,
                acabamento: descricao.descVestBal?.acabamento || null,
                tambores: descricao.descVestBal?.tambores || [],
                tamborInput: descricao.descVestBal?.tamborInput || null,
                carregadores: descricao.descVestBal?.carregadores || [],
                carregadorInput: descricao.descVestBal?.carregadorInput || null,
                capacidade: descricao.descVestBal?.capacidade || null,
                mecanDisps: descricao.descVestBal?.mecanDisps || [],
                mecanismoDisparoInput: descricao.descVestBal?.mecanismoDisparoInput || null,
                massaMira: descricao.descVestBal?.massaMira || null,
                alcaMira: descricao.descVestBal?.alcaMira || null,
                empunhaduras: descricao.descVestBal?.empunhaduras || [],
                empunhaduraInput: descricao.descVestBal?.empunhaduraInput || null,
                coronhas: descricao.descVestBal?.coronhas || [],
                coronhaInput: descricao.descVestBal?.coronhaInput || null,
                telhas: descricao.descVestBal?.telhas || [],
                telhaInput: descricao.descVestBal?.telhaInput || null,
                guardaMao: descricao.descVestBal?.guardaMao || null,
                gravacoes: descricao.descVestBal?.gravacoes || [],
                gravacoesInput: descricao.descVestBal?.gravacoesInput || null,
                outrasCaracteristicas: descricao.descVestBal?.outrasCaracteristicas || null,
                fabricante: descricao.descVestBal?.fabricante || null,
                coletaPadrao: descricao.descVestBal?.coletaPadrao || null,
                estadoConservacao: descricao.descVestBal?.estadoConservacao || null,
                capsulaEsps: descricao.descVestBal?.capsulaEsps || [],
                capsulaEspoletamentoInput: descricao.descVestBal?.capsulaEspoletamentoInput || null,
                tipoEstojos: descricao.descVestBal?.tipoEstojos || [],
                tipoEstojoInput: descricao.descVestBal?.tipoEstojoInput || null,
                tiposProjetil: descricao.descVestBal?.tiposProjetil || [],
                tipoProjetilInput: descricao.descVestBal?.tipoProjetilInput || null,
                ressaltosCavados: descricao.descVestBal?.ressaltosCavados || [],
                ressaltosCavadosInput: descricao.descVestBal?.ressaltosCavadosInput || null,
                tiposRaiamentos: descricao.descVestBal?.tiposRaiamentos || [],
                tiposRaiamentosInput: descricao.descVestBal?.tiposRaiamentosInput || null,
                tipoFabricacao: descricao.descVestBal?.tipoFabricacao || null,
              },
            }))
            break
          case 'QUIMICO':
            formDescricaoTecnica.setFormState((prevState) => ({
              ...prevState,
              values: {
                ...prevState.values,
                tipoVestigioGenerico: descricao.tipoVestigioGenerico,
                tipoVestigioEspecifico: descricao.tipoVestigioEspecifico,
                unidades: descricao.unidades,
                unidadesConsumidas: descricao.unidadesConsumidas,
                descricao: descricao.descricao,
                quantidade: descricao.descVestQuim?.quantidade || 0,
                unidadeQuantidade: descricao.descVestQuim?.unidadeQuantidade || null,
                tipoQuantidade: descricao.descVestQuim?.tipoQuantidade || null,
                quantificacaoContraprova: descricao.descVestQuim?.quantificacaoContraprova || 'RESTANTE',
                quantidadeContraprova: descricao.descVestQuim?.quantidadeContraprova || 0,
                unidadeQuantidadeContraprova: descricao.descVestQuim?.unidadeQuantidadeContraprova || null,
                lacreContraprova: descricao.descVestQuim?.lacreContraprova || null,
                anexo: descricao.descVestQuim?.anexo ? 'SIM' : 'NAO',
                lacreAnexo: descricao.descVestQuim?.lacreAnexo || null,
                tipoEmbalagemPrimaria: descricao.descVestQuim?.tipoEmbalagemPrimaria || null,
                outroTipoEmbalagemPrimaria: descricao.descVestQuim?.outroTipoEmbalagemPrimaria || null,
                corEmbalagemPrimaria: descricao.descVestQuim?.corEmbalagemPrimaria || null,
                outraCorEmbalagemPrimaria: descricao.descVestQuim?.outraCorEmbalagemPrimaria || null,
                tipoFechoEmbalagemPrimaria: descricao.descVestQuim?.tipoFechoEmbalagemPrimaria || null,
                outroTipoFechoEmbalagemPrimaria: descricao.descVestQuim?.outroTipoFechoEmbalagemPrimaria || null,
                logomarcaEmbalagemPrimaria: descricao.descVestQuim?.logomarcaEmbalagemPrimaria || null,
                tipoLogomarcaEmbalagemPrimaria: descricao.descVestQuim?.tipoLogomarcaEmbalagemPrimaria || null,
                corMaterial: descricao.descVestQuim?.corMaterial || null,
                outraCorMaterial: descricao.descVestQuim?.outraCorMaterial || null,
                logomarcaSubstancia: descricao.descVestQuim?.logomarcaSubstancia || null,
                tipoLogomarcaSubstancia: descricao.descVestQuim?.tipoLogomarcaSubstancia || null,
                outroAspectoFisico: descricao.descVestQuim?.outroAspectoFisico || null,
                aspFisics: descricao.descVestQuim?.aspFisics || [],
              },
            }))
            break
          default:
            formDescricaoTecnica.setFormState((prevState) => ({
              ...prevState,
              values: {
                ...prevState.values,
                tipoVestigioGenerico: descricao.tipoVestigioGenerico,
                tipoVestigioEspecifico: descricao.tipoVestigioEspecifico,
                unidades: descricao.unidades,
                unidadesConsumidas: descricao.unidadesConsumidas,
                descricao: descricao.descricao,
              },
            }))
            break
        }
      } catch (error) {
        console.error('Error prefilling form for duplication:', error)
      }
    }
  }, [descricaoADuplicar]) // Excluded descricaoTecnicaVestigioService to prevent loop

  useEffect(() => {
    preencherFormularioDuplicacaoVestigio()
  }, [preencherFormularioDuplicacaoVestigio])

  const handleClickCancelarOperacao = useCallback(() => {
    formDescricaoTecnica.setFormState((prevState) => ({
      ...prevState,
      values: {
        tipoVestigioGenerico: null,
        tipoVestigioEspecifico: null,
        unidades: 1,
        unidadesConsumidas: 0,
        descricao: null,
        quantidade: 0,
        unidadeQuantidade: null,
        tipoQuantidade: null,
        quantificacaoContraprova: 'RESTANTE',
        quantidadeContraprova: 0,
        unidadeQuantidadeContraprova: null,
        lacreContraprova: null,
        anexo: 'NAO',
        lacreAnexo: null,
        tipoEmbalagemPrimaria: null,
        outroTipoEmbalagemPrimaria: null,
        corEmbalagemPrimaria: null,
        outraCorEmbalagemPrimaria: null,
        tipoFechoEmbalagemPrimaria: null,
        outroTipoFechoEmbalagemPrimaria: null,
        logomarcaEmbalagemPrimaria: null,
        tipoLogomarcaEmbalagemPrimaria: null,
        corMaterial: null,
        outraCorMaterial: null,
        outroAspectoFisico: null,
        logomarcaSubstancia: null,
        tipoLogomarcaSubstancia: null,
        aspFisics: [],
        arquivos: [],

        //BALÍSTICO
        tipo: null,
        marca: null,
        modelo: null,
        calibreNominal: null,
        calibreVariacao: null,
        calibreCompativel: null,
        medidaMassa: null,
        medidaDiametro: null,
        medidaComprimento: null,
        locFabs: [],
        localFabricacaoInput: null,
        numeracaoDeSerie: null,
        locGravNumSers: [],
        localGravacaoNumeracaoSerieInput: null,
        numeracaoDeMontagem: null,
        locGravNumMons: [],
        localGravacaoNumeracaoMontagemInput: null,
        sisPercs: [],
        sistemaPercussaoInput: null,
        sisCarrs: [],
        sistemaCarregamentoInput: null,
        sistemaFuncionamento: null,
        almaCanos: [],
        almaCanoInput: null,
        comprimentoCano: null,
        acabamento: null,
        tambores: [],
        tamborInput: null,
        carregadores: [],
        carregadorInput: null,
        capacidade: null,
        mecanDisps: [],
        mecanismoDisparoInput: null,
        massaMira: null,
        alcaMira: null,
        empunhaduras: [],
        empunhaduraInput: null,
        coronhas: [],
        coronhaInput: null,
        telhas: [],
        telhaInput: null,
        guardaMao: null,
        gravacoes: [],
        gravacoesInput: null,
        outrasCaracteristicas: null,
        fabricante: null,
        coletaPadrao: null,
        estadoConservacao: null,

        capsulaEsps: [],
        capsulaEspoletamentoInput: null,
        tipoEstojos: [],
        tipoEstojoInput: null,
        tiposProjetil: [],
        tipoProjetilInput: null,
        ressaltosCavados: [],
        ressaltosCavadosInput: null,
        tiposRaiamentos: [],
        tiposRaiamentosInput: null,
        tipoFabricacao: null,
      },
      touched: {},
    }))
    setDescricaoADuplicar(null)
    setEditarDescricao(false)
    setOpenEditarDescricaoTecnica(false)
  }, [setOpenEditarDescricaoTecnica, setDescricaoADuplicar, setEditarDescricao])

  const handleChangeTipoVestigio = useCallback(
    (newValue) => {
      const valorSelecionado = tiposVestigios.find((tipo) => tipo.identificador === newValue)
      if (valorSelecionado) {
        setTiposVestigiosEspecificos(valorSelecionado.tiposEsps || [])
      } else {
        setTiposVestigiosEspecificos([])
      }
      formDescricaoTecnica.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          tipoVestigioGenerico: newValue,
          tipoVestigioEspecifico: null,
        },
      }))
    },
    [tiposVestigios, formDescricaoTecnica]
  )

  const handleConfirmarDescricaoTecnica = useCallback(async () => {
    try {
      await descricaoTecnicaVestigioService.cadastrarDescricaoTecnicaVestigioService(
        formDescricaoTecnica.formState.values.tipoVestigioGenerico,
        idVestigio,
        idExameRealizado,
        formDescricaoTecnica.formState.values,
        setProcessandoOperacao
      )
      if (editarDescricao) {
        setEditarDescricao(false)
        await descricaoTecnicaVestigioService.deletarDescricaoTecnicaVestigio(
          descricaoADuplicar.id,
          setProcessandoOperacao
        )
      }
    } catch (err) {
      console.error('Error saving technical description:', err)
    }

    if (setAtualizado) setAtualizado((prevState) => !prevState)
    formDescricaoTecnica.setFormState((prevState) => ({
      ...prevState,
      values: {
        tipoVestigioGenerico: null,
        tipoVestigioEspecifico: null,
        unidades: 1,
        unidadesConsumidas: 0,
        descricao: null,
        quantidade: 0,
        unidadeQuantidade: null,
        tipoQuantidade: null,
        quantificacaoContraprova: 'RESTANTE',
        quantidadeContraprova: 0,
        unidadeQuantidadeContraprova: null,
        anexo: 'NAO',
        lacreAnexo: null,
        lacreContraprova: null,
        tipoEmbalagemPrimaria: null,
        outroTipoEmbalagemPrimaria: null,
        corEmbalagemPrimaria: null,
        outraCorEmbalagemPrimaria: null,
        tipoFechoEmbalagemPrimaria: null,
        outroTipoFechoEmbalagemPrimaria: null,
        logomarcaEmbalagemPrimaria: null,
        tipoLogomarcaEmbalagemPrimaria: null,
        corMaterial: null,
        outraCorMaterial: null,
        logomarcaSubstancia: null,
        tipoLogomarcaSubstancia: null,
        outroAspectoFisico: null,
        aspFisics: [],
        arquivos: [],

        //BALÍSTICO
        tipo: null,
        marca: null,
        modelo: null,
        calibreNominal: null,
        calibreVariacao: null,
        calibreCompativel: null,
        medidaMassa: null,
        medidaDiametro: null,
        medidaComprimento: null,
        locFabs: [],
        localFabricacaoInput: null,
        numeracaoDeSerie: null,
        locGravNumSers: [],
        localGravacaoNumeracaoSerieInput: null,
        numeracaoDeMontagem: null,
        locGravNumMons: [],
        localGravacaoNumeracaoMontagemInput: null,
        sisPercs: [],
        sistemaPercussaoInput: null,
        sisCarrs: [],
        sistemaCarregamentoInput: null,
        sistemaFuncionamento: null,
        almaCanos: [],
        almaCanoInput: null,
        comprimentoCano: null,
        acabamento: null,
        tambores: [],
        tamborInput: null,
        carregadores: [],
        carregadorInput: null,
        capacidade: null,
        mecanDisps: [],
        mecanismoDisparoInput: null,
        massaMira: null,
        alcaMira: null,
        empunhaduras: [],
        empunhaduraInput: null,
        coronhas: [],
        coronhaInput: null,
        telhas: [],
        telhaInput: null,
        guardaMao: null,
        gravacoes: [],
        gravacoesInput: null,
        outrasCaracteristicas: null,
        fabricante: null,
        coletaPadrao: null,
        estadoConservacao: null,
        capsulaEsps: [],
        capsulaEspoletamentoInput: null,
        tipoEstojos: [],
        tipoEstojoInput: null,
        tiposProjetil: [],
        tipoProjetilInput: null,
        ressaltosCavados: [],
        ressaltosCavadosInput: null,
        tiposRaiamentos: [],
        tiposRaiamentosInput: null,
        tipoFabricacao: null,
      },
    }))
    fetchDescricoesTecnicasDeVestigio()
    setDescricaoADuplicar(null)
    setOpenEditarDescricaoTecnica(false)
  }, [
    formDescricaoTecnica.formState.values,
    idVestigio,
    idExameRealizado,
    editarDescricao,
    descricaoADuplicar,
    fetchDescricoesTecnicasDeVestigio,
    setOpenEditarDescricaoTecnica,
    setDescricaoADuplicar,
    setEditarDescricao,
    setAtualizado,
  ]) // Excluded services to prevent loop

  useEffect(() => {
    formDescricaoTecnica.setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        arquivos,
      },
    }))
  }, [arquivos])

  useEffect(() => {
    fetchTiposVestigios()
  }, [fetchTiposVestigios])

  useEffect(() => {
    fetchExameRealizado()
  }, [fetchExameRealizado])

  useEffect(() => {
    const {
      tipoVestigioGenerico,
      tipoVestigioEspecifico,
      tipo,
      descricao,
      unidades,
      unidadesConsumidas,
      anexo,
      quantidade,
      unidadeQuantidade,
      quantificacaoContraprova,
      quantidadeContraprova,
      unidadeQuantidadeContraprova,
      lacreContraprova,
    } = formDescricaoTecnica.formState.values

    const camposObrigatoriosFormulario = (generico, especifico) => {
      if (generico === 'BALISTICO') {
        switch (especifico) {
          case 'ARMA_DE_PRESSAO':
          case 'ARMA_DE_FOGO':
            return [
              'tipo',
              'tipoFabricacao',
              'marca',
              'modelo',
              ['calibreNominal', 'calibreCompativel'],
              ['locFabs', 'localFabricacaoInput'],
              'numeracaoDeSerie',
              ['locGravNumSers', 'localGravacaoNumeracaoSerieInput'],
              'numeracaoDeMontagem',
              ['sisPercs', 'sistemaPercussaoInput'],
              ['sisCarrs', 'sistemaCarregamentoInput'],
              'sistemaFuncionamento',
              ['almaCanos', 'almaCanoInput'],
              'comprimentoCano',
              'acabamento',
              ['mecanDisps', 'mecanismoDisparoInput'],
              'massaMira',
              'alcaMira',
            ]
          case 'CARTUCHO':
            return [
              'unidadesConsumidas',
              'unidades',
              'calibreNominal',
              'fabricante',
              ['capsulaEsps', 'capsulaEspoletamentoInput'],
              ['tipoEstojos', 'tipoEstojoInput'],
              ['tiposProjetil', 'tipoProjetilInput'],
            ]
          case 'ESTOJO':
            return [
              'unidades',
              'calibreNominal',
              'fabricante',
              ['capsulaEsps', 'capsulaEspoletamentoInput'],
              ['tipoEstojos', 'tipoEstojoInput'],
            ]
          case 'ELEMENTO_MUNICAO':
            return [
              'tipo',
              ['ressaltosCavados', 'ressaltosCavadosInput'],
              ['tiposProjetil', 'tipoProjetilInput'],
              ['tiposRaiamentos', 'tiposRaiamentosInput'],
              'medidaMassa',
              'medidaDiametro',
              'medidaComprimento',
              'calibreCompativel',
            ]
          case 'ELEMENTO_RECARGA':
            return ['tipo', 'quantidade', 'unidadeQuantidade', 'outrasCaracteristicas']
          case 'CARREGADOR':
            return ['unidades', 'tipo', 'capacidade', 'calibreNominal']
          default:
            return []
        }
      }
      return []
    }

    const camposObrigatorios = camposObrigatoriosFormulario(tipoVestigioGenerico, tipoVestigioEspecifico)
    const formularioInvalido = camposObrigatorios?.some((campo) => {
      if (typeof campo === 'string') {
        return (
          formDescricaoTecnica.formState.values[campo] === '' ||
          formDescricaoTecnica.formState.values[campo] === null
        )
      }
      if (Array.isArray(campo)) {
        if (
          campo.includes('locGravNumSers') &&
          formDescricaoTecnica.formState.values['tipoFabricacao']?.toUpperCase() === 'ARTESANAL'
        ) {
          return false
        }
        return !campo.some((campoInterno) => formDescricaoTecnica.formState.values[campoInterno]?.length)
      }
      return false
    })

    const isInvalido =
      formularioInvalido ||
      formDescricaoTecnica.hasErrorWithoutTouch('tipoVestigioGenerico') ||
      formDescricaoTecnica.hasErrorWithoutTouch('tipoVestigioEspecifico') ||
      ((tipoVestigioGenerico === 'GERAL' ||
        tipoVestigioGenerico === 'BIOLOGICO' ||
        tipoVestigioGenerico === 'ELETRONICO' ||
        tipoVestigioGenerico === 'DOCUMENTAL' ||
        tipoVestigioGenerico === 'PAPILOSCOPICO' ||
        tipoVestigioGenerico === 'MERCEOLOGICO') &&
        !descricao) ||
      (tipoVestigioGenerico === 'QUIMICO' &&
        (!unidades ||
          !quantidade ||
          !unidadeQuantidade ||
          (quantificacaoContraprova === 'ESPECIFICADO' &&
            (!quantidadeContraprova || !unidadeQuantidadeContraprova)) ||
          !anexo ||
          ((quantificacaoContraprova === 'ESPECIFICADO' || quantificacaoContraprova === 'RESTANTE') &&
            !lacreContraprova)))

    setIsFormularioInvalido(Boolean(isInvalido))
  }, [formDescricaoTecnica.formState])

  return {
    tiposVestigios,
    tiposVestigiosEspecificos,
    processandoTiposVestigios,
    processandoOperacao,
    isFormularioInvalido,
    exameRealizado,
    formDescricaoTecnica,
    handleClickCancelarOperacao,
    handleChangeTipoVestigio,
    handleConfirmarDescricaoTecnica,
  }
}
